/**
 * PlatformAdapter — the PLATFORM ABSTRACTION for BattleBrew's social layer.
 *
 * The game/lobby code must stay platform-independent: it never calls
 * `window.CrazyGames.SDK` directly. It asks the adapter for:
 *   - the platform type ('standalone' | 'crazygames')
 *   - the current CrazyGames user (for multiplayer display names)
 *   - multiplayer room registration (updateRoom / leftRoom)
 *   - invite links (the CrazyGames "Invite" button)
 *   - the live join-room listener (a friend joins while the game is open)
 *   - the startup invite params (a friend joined before the game loaded)
 *   - the Instant Multiplayer flag (launch straight into a lobby)
 *
 * The STANDALONE implementation (battlebrew.online / local dev) safely
 * no-ops every CrazyGames-specific function, so no SDK is required for
 * standalone gameplay. The CRAZYGAMES implementation (the /crazygames
 * subpage) is backed by the CrazyGames SDK v3 `game` module.
 *
 * This adapter REUSES the existing CrazyGamesService for the `user` module
 * (auth/token — the verified-JWT login flow). It only adds the `game`
 * module (multiplayer rooms / invites). It deliberately does NOT use the
 * deprecated `showInviteButton()` API — invites are built with
 * `game.inviteLink(...)`.
 */

import { CrazyGamesService, getGlobalCrazyGames, type CrazyGamesUser } from './CrazyGamesService';

// ─── Platform type ──────────────────────────────────────────────────────────

export type PlatformType = 'standalone' | 'crazygames';

// ─── The multiplayer room the game reports to the platform ─────────────────

/** A multiplayer room as reported to the platform (CrazyGames `updateRoom`). */
export interface MultiplayerRoom {
  /** The room's unique id within BattleBrew (the immutable lobby id). */
  roomId: string;
  /** Whether friends can join the room right now. */
  isJoinable: boolean;
  /** Routing info carried in the invite (NEVER auth credentials). */
  inviteParams: Record<string, string>;
}

// ─── The adapter interface ──────────────────────────────────────────────────

export interface PlatformAdapter {
  /** The platform type. */
  readonly type: PlatformType;
  /** True on CrazyGames (the /crazygames subpage). */
  readonly isCrazyGames: boolean;

  /** The current platform user (null when signed out / standalone). */
  getCurrentUser(): Promise<CrazyGamesUser | null>;

  /**
   * Register / update the current multiplayer room. Called whenever the
   * lobby's joinability changes. No-op on standalone.
   */
  updateMultiplayerRoom(room: MultiplayerRoom): void;

  /**
   * Leave the current multiplayer room (permanently). NOT called when a
   * game simply starts (the party survives) — only when the player exits
   * the room for good. No-op on standalone.
   */
  leaveMultiplayerRoom(): void;

  /**
   * Build an invite link carrying the given params (the "Invite" button).
   * Resolves to a URL. Rejects on standalone / when the SDK is unavailable.
   */
  createInviteLink(params: Record<string, string>): Promise<string>;

  /**
   * Register a listener for a friend joining the room WHILE the game is
   * running (CrazyGames `addJoinRoomListener`). The listener receives the
   * invite params (e.g. `{ lobbyCode }`). Returns a cleanup function that
   * removes the listener. No-op on standalone (returns a no-op cleanup).
   */
  onJoinRoom(listener: (inviteParams: Record<string, string>) => void): () => void;

  /**
   * The startup invite params (CrazyGames `game.inviteParams`) — present
   * when the player launched the game from a friend's invite. null when
   * absent / standalone. Read ONCE after SDK init.
   */
  getStartupInviteParams(): Record<string, string> | null;

  /**
   * True when the game was launched via CrazyGames Instant Multiplayer.
   * Read ONCE after SDK init (must be checked AFTER the invite params —
   * an invited player must join their friend's lobby, not create a new one).
   */
  isInstantMultiplayer(): boolean;

  /**
   * Mark the startup invite params as consumed (clear the one-time URL
   * params after the app has joined the invited lobby). No-op on
   * standalone.
   */
  clearInviteParams(): void;

  /**
   * Mark the Instant Multiplayer flag as consumed (after the app has
   * created the startup lobby). No-op on standalone.
   */
  clearInstantMultiplayer(): void;
}

// ─── The CrazyGames SDK `game` module surface we use ────────────────────────

// ─── Development logging (easy to disable for production) ──────────────────────

/**
 * Dev logging switch: set to `false` for production builds (every log
 * call below becomes a no-op — grep for CG_DEV_LOG to audit).
 */
export const CG_DEV_LOG = true;

function cgLog(tag: string, ...args: unknown[]): void {
  if (!CG_DEV_LOG) return;
  console.log(`[CrazyGames] ${tag}`, ...args);
}

// ─── Lobby joinability (the single source of truth) ────────────────────────────

/**
 * Whether the platform should advertise the room as joinable. Computed
 * from the SERVER-SUPPLIED lobby state (the client never infers capacity
 * or state from local cache alone):
 *   - WAITING / COUNTDOWN / POST_GAME with a free seat → joinable
 *   - full (players >= capacity) → not joinable
 *   - IN_GAME (any capacity) → not joinable
 */
export function isLobbyJoinable(lobby: {
  state: string;
  playerCount: number;
  capacity: number;
}): boolean {
  return (
    (lobby.state === 'WAITING' ||
      lobby.state === 'COUNTDOWN' ||
      lobby.state === 'POST_GAME') &&
    lobby.playerCount < lobby.capacity
  );
}

export interface CrazyGamesGameModule {
  /** Register / update the current room (joinability + invite params). */
  updateRoom(params: {
    roomId: string;
    isJoinable?: boolean;
    inviteParams?: Record<string, string>;
  }): void;
  /** Permanently leave the current room. */
  leftRoom(): void;
  /** Build an invite link from the given params. */
  inviteLink(params: Record<string, string>): string;
  /** Register a live join-room listener. */
  addJoinRoomListener(listener: (inviteParams: Record<string, string>) => void): void;
  /** Remove a previously registered listener. */
  removeJoinRoomListener(listener: (inviteParams: Record<string, string>) => void): void;
  /** The startup invite params (null when none). */
  readonly inviteParams: Record<string, string> | null;
  /** True when launched via Instant Multiplayer. */
  readonly isInstantMultiplayer: boolean;
}

/** Read the SDK `game` module (null before init / when unavailable). */
export function getGlobalCrazyGamesGame(): CrazyGamesGameModule | null {
  const g = globalThis as {
    CrazyGames?: { SDK?: { game?: unknown } };
  };
  return (g.CrazyGames?.SDK?.game ?? null) as CrazyGamesGameModule | null;
}

// ─── The standalone platform (no-op for all CrazyGames functions) ──────────

/**
 * The standalone platform (battlebrew.online / local dev). Every
 * CrazyGames-specific function is a safe no-op, so the game works with no
 * SDK present. `getCurrentUser` resolves null.
 */
export class StandalonePlatform implements PlatformAdapter {
  readonly type: PlatformType = 'standalone';
  readonly isCrazyGames = false;

  getCurrentUser(): Promise<CrazyGamesUser | null> {
    return Promise.resolve(null);
  }
  updateMultiplayerRoom(_room: MultiplayerRoom): void {
    /* no-op — no platform room on standalone */
  }
  leaveMultiplayerRoom(): void {
    /* no-op */
  }
  createInviteLink(_params: Record<string, string>): Promise<string> {
    return Promise.reject(new Error('Invites are not available on this platform.'));
  }
  onJoinRoom(_listener: (inviteParams: Record<string, string>) => void): () => void {
    return () => {
      /* no-op cleanup */
    };
  }
  getStartupInviteParams(): Record<string, string> | null {
    return null;
  }
  isInstantMultiplayer(): boolean {
    return false;
  }
  clearInviteParams(): void {
    /* no-op — no startup params on standalone */
  }
  clearInstantMultiplayer(): void {
    /* no-op — no instant multiplayer on standalone */
  }
}

// ─── The CrazyGames platform ────────────────────────────────────────────────

export interface CrazyGamesPlatformOptions {
  /** A pre-initialized SDK `game` module (tests). */
  game?: CrazyGamesGameModule | null;
  /** The auth service (for getCurrentUser). */
  authService?: CrazyGamesService;
}

/**
 * The CrazyGames platform — backed by the CrazyGames SDK v3 `game` module.
 * The `game` module is read lazily (after `SDK.init()`); if it is absent
 * (SDK not loaded / unavailable), every call degrades to a safe no-op so
 * the game keeps working.
 */
export class CrazyGamesPlatform implements PlatformAdapter {
  readonly type: PlatformType = 'crazygames';
  readonly isCrazyGames = true;

  private _game: CrazyGamesGameModule | null;
  private readonly _authService: CrazyGamesService;
  // One-time startup flags (consumed after the app reads them once).
  private _inviteParamsConsumed = false;
  private _instantMultiplayerConsumed = false;

  constructor(opts: CrazyGamesPlatformOptions = {}) {
    // An explicit game module wins (tests); otherwise read the global.
    this._game =
      opts.game !== undefined
        ? opts.game
        : getGlobalCrazyGamesGame();
    this._authService = opts.authService ?? new CrazyGamesService();
  }

  /** Refresh the game module reference (call after SDK init / on demand). */
  refresh(): void {
    if (this._game === null) {
      this._game = getGlobalCrazyGamesGame();
    }
  }

  /**
   * The `game` module, re-read from the global on demand while it is
   * still null (the adapter may be installed before SDK.init() finished;
   * once a module is found it is cached). Null when the SDK is really
   * unavailable — every call site degrades to a safe no-op.
   */
  private _gameModule(): CrazyGamesGameModule | null {
    if (this._game === null) {
      this._game = getGlobalCrazyGamesGame();
    }
    return this._game;
  }

  getCurrentUser(): Promise<CrazyGamesUser | null> {
    // The auth service resolves the signed-in user WITHOUT prompting.
    return this._authService.getUser().catch(() => null);
  }

  updateMultiplayerRoom(room: MultiplayerRoom): void {
    const game = this._gameModule();
    if (!game) {
      cgLog('updateRoom skipped — SDK `game` module unavailable', room);
      return; // SDK unavailable — safe no-op
    }
    const payload = {
      roomId: room.roomId,
      isJoinable: room.isJoinable,
      inviteParams: room.inviteParams,
    };
    cgLog('updateRoom', payload, 'environment:', getGlobalCrazyGames()?.environment);
    try {
      game.updateRoom(payload);
      cgLog('updateRoom successful', { roomId: room.roomId, isJoinable: room.isJoinable });
    } catch (err) {
      cgLog('updateRoom failed', err);
    }
  }

  leaveMultiplayerRoom(): void {
    const game = this._gameModule();
    if (!game) {
      cgLog('leftRoom skipped — SDK `game` module unavailable');
      return;
    }
    cgLog('leftRoom', 'environment:', getGlobalCrazyGames()?.environment);
    try {
      game.leftRoom();
      cgLog('leftRoom successful');
    } catch (err) {
      cgLog('leftRoom failed', err);
    }
  }

  createInviteLink(params: Record<string, string>): Promise<string> {
    const game = this._gameModule();
    if (!game) {
      cgLog('inviteLink failed — SDK `game` module unavailable');
      return Promise.reject(new Error('The CrazyGames SDK is not available.'));
    }
    try {
      cgLog('inviteLink', params);
      const link = game.inviteLink(params);
      cgLog('inviteLink successful');
      return Promise.resolve(link);
    } catch (err) {
      cgLog('inviteLink failed', err);
      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  onJoinRoom(listener: (inviteParams: Record<string, string>) => void): () => void {
    const game = this._gameModule();
    if (!game) {
      cgLog('addJoinRoomListener skipped — SDK `game` module unavailable');
      return () => {
        /* no-op cleanup */
      };
    }
    try {
      cgLog('addJoinRoomListener');
      game.addJoinRoomListener(listener);
      cgLog('addJoinRoomListener successful');
    } catch (err) {
      cgLog('addJoinRoomListener failed', err);
      return () => {
        /* no-op cleanup */
      };
    }
    return () => {
      try {
        cgLog('removeJoinRoomListener');
        game.removeJoinRoomListener(listener);
      } catch {
        /* best-effort removal */
      }
    };
  }

  getStartupInviteParams(): Record<string, string> | null {
    if (this._inviteParamsConsumed) return null;
    return this._gameModule()?.inviteParams ?? null;
  }

  isInstantMultiplayer(): boolean {
    if (this._instantMultiplayerConsumed) return false;
    return this._gameModule()?.isInstantMultiplayer ?? false;
  }

  clearInviteParams(): void {
    this._inviteParamsConsumed = true;
  }

  clearInstantMultiplayer(): void {
    this._instantMultiplayerConsumed = true;
  }
}

// ─── Factory ────────────────────────────────────────────────────────────────

export interface CreatePlatformOptions {
  /** A pre-initialized CrazyGames `game` module (tests / on-platform). */
  game?: CrazyGamesGameModule | null;
  /** The CrazyGames auth service (tests). */
  authService?: CrazyGamesService;
}

/**
 * Create the platform adapter for the current context. On the CrazyGames
 * subpage (a `game` module is available) it returns a CrazyGamesPlatform;
 * everywhere else a StandalonePlatform. The caller (main.ts) detects the
 * CrazyGames context via the existing isCrazyGamesContext and passes the
 * SDK `game` module here after `SDK.init()`.
 */
export function createPlatform(opts: CreatePlatformOptions = {}): PlatformAdapter {
  if (opts.game) {
    return new CrazyGamesPlatform({ game: opts.game, authService: opts.authService });
  }
  return new StandalonePlatform();
}

/** The shared default adapter (standalone until the game module is wired). */
let _defaultPlatform: PlatformAdapter = new StandalonePlatform();

/**
 * Install the platform adapter (called once at startup, after SDK init on
 * the CrazyGames subpage). The rest of the app reads `getPlatform()`.
 */
export function setPlatform(platform: PlatformAdapter): void {
  _defaultPlatform = platform;
}

/** The current platform adapter (standalone by default). */
export function getPlatform(): PlatformAdapter {
  return _defaultPlatform;
}
